import React, { Suspense, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { slide as SlideMenu } from 'react-burger-menu';
import { useImage } from 'react-image';
import { Button, NavLink } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleUp,
  faAngleDown,
  faSignOutAlt,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import css from 'styled-jsx/css';

import BrandingContext from '../../Context/BrandingContext';
import IconsContext from '../../Context/IconsContext';
import { TOOLTIP_OVERRIDE } from '../../Styles/Z-INDEX';

const staticStyles = {
  bmBurgerButton: { height: '15px', left: '24px', top: '27px', width: '18px' },
  bmCrossButton: { height: '24px', width: '24px' },
  bmMenuWrap: { zIndex: TOOLTIP_OVERRIDE },
};

export function Menu({ children, updateMenuState, isOpen }) {
  const { primary } = useContext(BrandingContext);

  const styles = {
    ...staticStyles,
    bmBurgerBarsHover: { background: primary },
  };

  return (
    <SlideMenu
      burgerBarClassName="bg-text-dark"
      burgerButtonClassName="position-fixed"
      crossClassName="text-muted"
      isOpen={isOpen}
      itemListClassName="pb-0 pt-3 px-3 text-text-light-gray d-flex flex-column"
      menuClassName="bg-white overflow-auto normal-text"
      onStateChange={updateMenuState}
      styles={styles}
      width={250}
    >
      {children}
    </SlideMenu>
  );
}

Menu.propTypes = {
  children: PropTypes.node,
  updateMenuState: PropTypes.func,
  isOpen: PropTypes.bool,
};

function ProfileImage({ profileImage }) {
  const { isSupraUi, isVersatileUi } = useContext(BrandingContext);
  const { avatar } = useContext(IconsContext);
  const { src } = useImage({ srcList: [profileImage, avatar] });

  switch (true) {
    case (isSupraUi || isVersatileUi) && !profileImage:
      return <FontAwesomeIcon className="mr-3" icon={faUser} size="2x" />;
    default: {
      return (
        <img
          alt="profile"
          className="mr-3 rounded-circle"
          height="40"
          src={src}
          width="40"
        />
      );
    }
  }
}
ProfileImage.propTypes = { profileImage: PropTypes.string };

export function UserNameAndProfile({ user, rootOrgName }) {
  const { _id, firstName, lastName, profileImage } = user || {};
  return _id ? (
    <div className="d-flex align-items-center px-3 py-4 mx-n3 mt-n3 bg-background-gray">
      <Suspense>
        <ProfileImage profileImage={profileImage} />
      </Suspense>
      <div className="flex-fill d-flex flex-column text-body">
        <p className="mb-0">{`${firstName || ''} ${lastName || ''}`}</p>
        <p className="font-weight-normal mb-0">{rootOrgName}</p>
      </div>
    </div>
  ) : null;
}

UserNameAndProfile.propTypes = {
  rootOrgName: PropTypes.string,
  user: PropTypes.object,
};

const { className: navLinkClass, styles: navLinkStyles } = css.resolve`
  a,
  div {
    color: var(--text-muted);
  }
  a:hover,
  button:hover,
  .active {
    color: var(--brand-primary);
  }

  button {
    background: var(--white) !important;
    color: var(--text-muted);
    cursor: pointer;
  }
  button:focus {
    box-shadow: none;
  }
`;

export function CollapseButton({
  children,
  customStyles,
  icon,
  isCollapsed,
  toggleCollapse,
}) {
  return (
    <Button
      className={`${navLinkClass} p-0 w-100 text-left text-decoration-none mt-3 d-flex align-items-center`}
      style={{ outlineColor: 'transparent', ...customStyles }}
      color="link"
      onClick={toggleCollapse}
    >
      {icon ? <FontAwesomeIcon icon={icon} fixedWidth /> : null}
      <span className="ml-4 flex-grow-1">{children}</span>
      <FontAwesomeIcon icon={isCollapsed ? faAngleUp : faAngleDown} />
      {navLinkStyles}
    </Button>
  );
}

CollapseButton.propTypes = {
  children: PropTypes.node,
  customStyles: PropTypes.object,
  icon: PropTypes.object,
  isCollapsed: PropTypes.bool,
  toggleCollapse: PropTypes.func,
};

export function TopNavLink({
  icon = faAngleUp,
  path = '',
  isActive,
  children,
  toggleSlideMenu,
  ...rest
}) {
  return (
    <NavLink
      tag={Link}
      to={path}
      className={`${navLinkClass} p-0 mt-3 text-decoration-none`}
      active={isActive}
      onClick={toggleSlideMenu}
      {...rest}
    >
      <FontAwesomeIcon icon={icon} fixedWidth />
      <span className="ml-4">{children}</span>
      {navLinkStyles}
    </NavLink>
  );
}

TopNavLink.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.object,
  isActive: PropTypes.bool,
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  replace: PropTypes.bool,
  toggleSlideMenu: PropTypes.func,
};

const { className: subLinkClass, styles: subLinkStyles } = css.resolve`
  a {
    color: var(--black-text);
  }
  a:hover,
  button:hover,
  .active {
    color: var(--brand-primary);
  }
`;

export function SubNavLink({ path = '', toggleMenu, children, isActive }) {
  return (
    <NavLink
      active={isActive}
      className={`${subLinkClass} text-decoration-none ml-5 py-1 font-weight-light three-quarter-text`}
      disabled={isActive}
      onClick={toggleMenu}
      tag={Link}
      to={path}
    >
      {children}
      {subLinkStyles}
    </NavLink>
  );
}

SubNavLink.propTypes = {
  children: PropTypes.node,
  isActive: PropTypes.bool,
  path: PropTypes.string,
  toggleMenu: PropTypes.func,
};

export function HelpLink({ url, toggle, children }) {
  return (
    <div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${subLinkClass} text-decoration-none ml-5 py-1 font-weight-light three-quarter-text`}
        onClick={toggle}
      >
        {children}
        {subLinkStyles}
      </a>
    </div>
  );
}

HelpLink.propTypes = {
  url: PropTypes.string,
  toggle: PropTypes.func,
  children: PropTypes.node,
};

export function LogoutButton({ logout }) {
  return (
    <div className={`${navLinkClass} p-0 mt-auto mb-3 text-decoration-none`}>
      <button className="btn btn-outline-secondary w-100 p-0" onClick={logout}>
        <FontAwesomeIcon icon={faSignOutAlt} fixedWidth />
        <span className="ml-4">Log Out</span>
      </button>
    </div>
  );
}

LogoutButton.propTypes = { logout: PropTypes.func };
